import type { ReportEnvironment } from "../hooks/useReportEnvironment"
import { reportErrorText, unpackNotebook } from "./reportClient"
import { latestNotebookRevision } from "./reportRecipe"
import type { Cadence, Notebook, ReportConfig, Schedule } from "./reportTypes"

interface RecipeRunResult {
  status?: string
  notebook_id?: string
  error?: string
  message?: string
}

export function createReportRecipeActions({
  request,
  run,
  config,
  notebook,
  persistDraft,
  refreshBootstrap,
  automaticScheduling,
  scheduledMessage,
  onNotice,
  onNotebook,
  onRemoved,
  onScheduleUpdated,
}: {
  request: ReportEnvironment["request"]
  run: (
    label: string,
    operation: (signal: AbortSignal) => Promise<void>,
  ) => Promise<void>
  config: ReportConfig
  notebook: Notebook | null
  persistDraft: (signal: AbortSignal) => Promise<Notebook>
  refreshBootstrap: (signal: AbortSignal) => Promise<unknown>
  automaticScheduling: boolean
  scheduledMessage: ReportEnvironment["messages"]["scheduled"]
  onNotice: (message: string) => void
  onNotebook: (notebook: Notebook) => void
  onRemoved: (id: string) => void
  onScheduleUpdated: (schedule: Schedule) => void
}) {
  // The write already succeeded; a failed list refresh must not report it as failed.
  const refreshQuietly = (signal: AbortSignal) =>
    refreshBootstrap(signal).catch(() => undefined)
  return {
    setEnabled: (schedule: Schedule, enabled: boolean) =>
      void run(
        enabled ? "Enabling schedule" : "Pausing schedule",
        async (signal) => {
          if (!schedule.updated_at) {
            throw new Error("Reload this recipe before changing its schedule.")
          }
          const updated = await request<Schedule>(
            `/api/schedules/${encodeURIComponent(schedule.id)}`,
            { enabled, expected_updated_at: schedule.updated_at },
            "PATCH",
            signal,
          )
          // The write already succeeded; keep the mounted row current even when
          // the refresh below fails, so a retry sends the new saved version.
          if (!signal.aborted) {
            onScheduleUpdated({ ...schedule, ...updated })
          }
          await refreshQuietly(signal)
          if (!signal.aborted) {
            onNotice(
              enabled
                ? `Automatic ${schedule.cadence} notebooks enabled.`
                : "Automatic notebooks paused. Saved notebooks remain available.",
            )
          }
        },
      ),
    /**
     * Repeats a freshly generated notebook: the schedule reuses its recipe and
     * freezes it as the template. It saves no dates, so every run covers the
     * period that just ended rather than the dates picked for the first one.
     */
    schedule: async (
      template: Notebook,
      cadence: Cadence,
      signal: AbortSignal,
      recipe: ReportConfig = config,
      existingSchedule?: Schedule | null,
    ): Promise<{ schedule: Schedule; message: string }> => {
      const existingTemplateId = existingSchedule?.template_notebook_id ?? null
      const keepsExistingTemplate =
        existingTemplateId !== null && existingTemplateId !== template.id
      const submitted = {
        ...(existingSchedule
          ? {
              id: existingSchedule.id,
              expected_updated_at: existingSchedule.updated_at,
            }
          : {}),
        scope: recipe.scope,
        cadence,
        instructions: recipe.instructions,
        name: existingSchedule?.name ?? template.title,
        // Scheduling again resumes a paused schedule, so the "runs {cadence}"
        // notice stays true; it never pauses one that is already running.
        enabled: automaticScheduling || existingSchedule?.enabled === true,
        template_notebook_id: existingTemplateId ?? template.id,
        template_revision: keepsExistingTemplate
          ? (existingSchedule?.template_revision ?? null)
          : latestNotebookRevision(template),
      }
      let schedule: Schedule
      try {
        const saved = await request<Partial<Schedule> & { id: string }>(
          "/api/schedules",
          submitted,
          "POST",
          signal,
        )
        // Shown before the next list refresh, so fields the reply omits come
        // from what was submitted; anything it returns, an explicit null
        // template included, wins.
        schedule = { ...submitted, ...saved }
      } catch (failure: unknown) {
        throw new Error(
          `The notebook is saved, but its schedule is not: ${reportErrorText(failure)}`,
        )
      }
      return {
        schedule,
        message: automaticScheduling
          ? scheduledMessage(cadence)
          : "Schedule saved. Automatic runs are not enabled here yet; generate each new notebook from Schedules.",
      }
    },
    remove: (id: string) =>
      void run("Deleting recipe", async (signal) => {
        await request(
          `/api/schedules/${encodeURIComponent(id)}`,
          {},
          "DELETE",
          signal,
        )
        // The delete already succeeded, so reconcile the mounted list even when the refresh below fails.
        if (!signal.aborted) {
          onRemoved(id)
        }
        await refreshQuietly(signal)
        if (!signal.aborted) {
          onNotice("Recipe deleted. Saved notebooks are unchanged.")
        }
      }),
    generate: (id: string) =>
      void run("Generating notebook", async (signal) => {
        if (notebook) {
          await persistDraft(signal)
        }
        const result = await request<RecipeRunResult>(
          `/api/schedules/${encodeURIComponent(id)}/run`,
          {},
          "POST",
          signal,
        )
        if (!result.notebook_id) {
          // A failed or already-attempted run carries its reason instead of a
          // notebook; loading "/notebooks/undefined" would only surface a 404.
          throw new Error(
            result.error ||
              result.message ||
              "The recipe did not produce a notebook. Try again or adjust the recipe.",
          )
        }
        const loaded = unpackNotebook(
          await request<Notebook>(
            `/api/notebooks/${encodeURIComponent(result.notebook_id)}`,
            undefined,
            "GET",
            signal,
          ),
        )
        if (signal.aborted) {
          return
        }
        onNotebook(loaded)
        await refreshQuietly(signal)
        if (!signal.aborted) {
          onNotice(
            "Notebook saved. The recipe remains available to generate again.",
          )
        }
      }),
  }
}
